/*
 * JK | Review Form
 * Validates the required fields with inline messages, previews uploads and handles
 * drag and drop. On a valid submit it sends the review to the Pathos reviews dashboard:
 *   1. each photo/video: POST {api}/uploads for a signed URL, then PUT the file there
 *   2. POST {api} with the review fields and the upload ids
 * The API address comes from the section settings (data-api).
 */
if (!customElements.get('jk-review-form')) {
  customElements.define(
    'jk-review-form',
    class JkReviewForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.header = this.querySelector('.jk-rform__header');
        this.thanks = this.querySelector('[data-thanks]');
        this.submitButton = this.querySelector('[data-submit]');
        this.submitLabel = this.submitButton ? this.submitButton.textContent : '';
        this.submitError = this.querySelector('[data-submit-error]');
        this.api = (this.dataset.api || '').replace(/\/+$/, '');
        this.ratingHint = this.querySelector('[data-rating-hint]');
        this.fileInput = this.querySelector('input[type="file"]');
        this.fileList = this.querySelector('[data-file-list]');
        this.drop = this.querySelector('[data-drop]');
        this.files = [];
        // Upload ids already issued for a file, so a retry after a failed send doesn't re-upload.
        this.uploaded = new WeakMap();
        this.sending = false;
        // The dashboard's own limits: 5 files, 250 MB each.
        this.maxFiles = Math.min(parseInt(this.dataset.maxFiles, 10) || 5, 5);
        this.maxBytes = Math.min(parseFloat(this.dataset.maxMb) || 250, 250) * 1024 * 1024;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.addEventListener('input', (event) => this.clearError(event.target));
        this.form.addEventListener('change', (event) => {
          this.clearError(event.target);
          if (event.target.name === 'rating') this.updateRatingHint();
        });

        if (this.fileInput) this.setupUpload();
      }

      updateRatingHint() {
        const checked = this.form.querySelector('input[name="rating"]:checked');
        if (!this.ratingHint || !checked) return;
        this.ratingHint.textContent = checked.dataset.label;
      }

      fieldFor(element) {
        return element.closest('.jk-rform__field');
      }

      clearError(element) {
        const field = element && this.fieldFor(element);
        if (field && field.classList.contains('is-invalid') && this.isFieldValid(field)) {
          field.classList.remove('is-invalid');
          // Everything fixed: drop the "Please complete" note under the button.
          if (!this.form.querySelector('.jk-rform__field.is-invalid')) this.showError('');
        }
      }

      isFieldValid(field) {
        const radios = field.querySelectorAll('input[type="radio"]');
        if (radios.length) return Array.from(radios).some((radio) => radio.checked);
        const control = field.querySelector('input:not([type="file"]), textarea');
        if (!control) return true;
        if (control.tagName === 'TEXTAREA' && control.required) {
          return control.value.trim().length >= (control.minLength > 0 ? control.minLength : 1);
        }
        return control.checkValidity() && (!control.required || control.value.trim() !== '');
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.sending) return;
        this.showError('');

        const invalid = [];
        this.form.querySelectorAll('.jk-rform__field[data-required]').forEach((field) => {
          const valid = this.isFieldValid(field);
          field.classList.toggle('is-invalid', !valid);
          if (!valid) invalid.push(field);
        });
        const firstInvalid = invalid[0];

        if (firstInvalid) {
          // Say what's missing right where they clicked, so something always visibly happens...
          const missing = invalid.map((field) => {
            const label = field.querySelector('.jk-rform__label');
            return label ? label.childNodes[0].textContent.trim() : '';
          }).filter(Boolean);
          const template = this.dataset.incompleteMessage || 'Please complete: [fields]';
          this.showError(template.replace('[fields]', missing.join(', ')));

          // ...then bring the first missing field into view. Focus waits for the scroll to
          // finish: focusing straight away cancels smooth scrolling in some browsers (Safari).
          this.scrollToField(firstInvalid);
          return;
        }

        this.send();
      }

      scrollToField(field) {
        // Leave room for the sticky header so the label isn't hidden under it.
        const header = document.querySelector('.section-header, sticky-header, .header-wrapper');
        const offset = (header ? header.getBoundingClientRect().height : 0) + 24;
        const top = field.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        const focusTarget = field.querySelector('input:not([type="file"]), textarea');
        if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 600);
      }

      collectFields() {
        const value = (selector) => {
          const element = this.form.querySelector(selector);
          return element ? element.value.trim() : '';
        };
        const rating = this.form.querySelector('input[name="rating"]:checked');
        const product = this.form.querySelector('input[name="product"]:checked');

        return {
          rating: rating ? Number(rating.value) : null,
          name: value('input[name="name"]'),
          email: value('input[name="email"]'),
          purchasedOn: value('input[name="purchase_date"]') || null,
          product: product ? product.value : null,
          productHandle: product && product.dataset.handle ? product.dataset.handle : null,
          body: value('textarea[name="review"]').slice(0, 2000),
        };
      }

      async send() {
        const fields = this.collectFields();
        this.setBusy(true, this.files.length ? this.dataset.uploadingLabel : this.dataset.sendingLabel);

        try {
          const mediaIds = [];
          for (const file of this.files) {
            mediaIds.push(await this.upload(file));
          }

          this.setBusy(true, this.dataset.sendingLabel);
          const response = await fetch(this.api, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...fields, mediaIds }),
          });
          const data = await this.readJson(response);
          if (!response.ok || !data.ok) throw new Error(data.error);

          this.showThanks();
        } catch (error) {
          this.showError((error && error.message) || this.dataset.errorMessage);
        } finally {
          this.setBusy(false);
        }
      }

      async upload(file) {
        if (this.uploaded.has(file)) return this.uploaded.get(file);

        const response = await fetch(`${this.api}/uploads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: file.name, contentType: file.type, size: file.size }),
        });
        const slot = await this.readJson(response);
        if (!response.ok || !slot.uploadUrl) throw new Error(slot.error);

        // Content-Type must match the signed URL exactly, and nothing else may be sent.
        const put = await fetch(slot.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': slot.contentType },
          body: file,
        });
        if (!put.ok) throw new Error(`${file.name} didn't upload. Please try again.`);

        this.uploaded.set(file, slot.id);
        return slot.id;
      }

      async readJson(response) {
        try {
          return await response.json();
        } catch (error) {
          return {};
        }
      }

      setBusy(busy, label) {
        this.sending = busy;
        if (!this.submitButton) return;
        this.submitButton.disabled = busy;
        this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.submitButton.textContent = busy ? label || this.submitLabel : this.submitLabel;
      }

      showError(message) {
        if (!this.submitError) return;
        this.submitError.textContent = message;
        this.submitError.hidden = !message;
      }

      showThanks() {
        this.form.hidden = true;
        if (this.header) this.header.hidden = true;
        if (!this.thanks) return;
        this.thanks.hidden = false;
        this.thanks.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.thanks.focus({ preventScroll: true });
      }

      setupUpload() {
        this.fileInput.addEventListener('change', () => {
          this.addFiles(this.fileInput.files);
          this.fileInput.value = '';
        });

        ['dragenter', 'dragover'].forEach((type) =>
          this.drop.addEventListener(type, (event) => {
            event.preventDefault();
            this.drop.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((type) =>
          this.drop.addEventListener(type, (event) => {
            event.preventDefault();
            this.drop.classList.remove('is-dragover');
          })
        );
        this.drop.addEventListener('drop', (event) => this.addFiles(event.dataTransfer.files));
      }

      addFiles(fileList) {
        const error = this.querySelector('[data-upload-error]');
        const rejected = [];

        Array.from(fileList).forEach((file) => {
          const isMedia = file.type.startsWith('image/') || file.type.startsWith('video/');
          if (!isMedia || file.size > this.maxBytes || this.files.length >= this.maxFiles) {
            rejected.push(file.name);
            return;
          }
          this.files.push(file);
        });

        if (error) error.closest('.jk-rform__field').classList.toggle('is-invalid', rejected.length > 0);
        this.renderFiles();
      }

      renderFiles() {
        this.fileList.querySelectorAll('[data-url]').forEach((item) => URL.revokeObjectURL(item.dataset.url));
        this.fileList.innerHTML = '';

        this.files.forEach((file, index) => {
          const url = URL.createObjectURL(file);
          const item = document.createElement('li');
          item.className = 'jk-rform__file';
          item.dataset.url = url;

          const preview = document.createElement(file.type.startsWith('video/') ? 'video' : 'img');
          preview.src = url;
          if (preview.tagName === 'IMG') preview.alt = file.name;
          else preview.muted = true;

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'jk-rform__file-remove';
          remove.setAttribute('aria-label', `Remove ${file.name}`);
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            if (this.sending) return;
            this.files.splice(index, 1);
            this.renderFiles();
          });

          item.append(preview, remove);
          this.fileList.append(item);
        });
      }
    }
  );
}
